import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import * as Yup from 'yup';

import RatingStars from '~components/rating-stars';
import { TextareaControlled } from '~components/forms/textarea';
import Button from '~components/button';
import Form from '~components/forms/form';
import NotificationsActions from '~redux/notifications';
import { createReview, updateReview, getMutualReview } from '~services/reviews';

import PromptService from '~services/prompt';

const MobileInlineChatReview = ({ 
  authorId, 
  receiverId,
  receiverFirstName,
  onClose 
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [existingReview, setExistingReview] = useState(null);

  const MAX_COMMENT_LENGTH = 200;
  
  const validationSchema = Yup.object().shape({
    score: Yup.number().required().min(1).max(5),
    comment: Yup.string().max(MAX_COMMENT_LENGTH).noEmailInContent().noPhoneInContent().noUrlInContent(),
  });

  useEffect(() => {
    const fetchReview = async () => {
      setIsLoading(true);
      try {
        const review = await getMutualReview({ authorId, receiverId });
        setExistingReview(review);
        if (review) {
          setRating(review.score);

          methods.reset({
            score: review.score,
            comment: review.text,
          });
        }
      } catch (err) {
        console.error('Error fetching review:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReview();
  }, [authorId, receiverId]);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      score: existingReview?.score || 0,
      comment: existingReview?.text || '',
    },
  });

  const handleRatingChange = (value) => {
    setRating(value);
    methods.setValue('score', value, { shouldValidate: true });
    
    // Expand the form when stars are clicked or rating changes
    setIsExpanded(true);
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      
      const reviewData = {
        authorId,
        receiverId,
        score: rating,
        text: formData.comment,
        token
      };

      if (existingReview) {
        await updateReview({ ...reviewData, reviewId: existingReview.id });
      } else {
        await createReview(reviewData);
      }

      dispatch(
        NotificationsActions.addNotification(
          intl.formatMessage({ 
            id: 'containers.review.notification', 
            defaultMessage: 'Review saved successfully.' 
          })
        )
      );
      
      // Just collapse the form after successful submission
      // instead of closing it completely
      setIsExpanded(false);
      
      // Update the existingReview state to reflect the new review
      setExistingReview({
        ...existingReview,
        id: existingReview?.id,
        score: rating,
        text: formData.comment
      });

      methods.reset({
        score: rating,
        comment: formData.comment
      });
      
      // IMPORTANT: Do NOT call onClose here, since we want to keep the stars visible
      // if (onClose) {
      //   onClose();
      // }
    } catch (err) {
      console.log('Could not submit review', err);
      PromptService.error(err);
      // dispatch(
      //   NotificationsActions.addNotification(
      //     intl.formatMessage({ 
      //       id: 'containers.review.error', 
      //       defaultMessage: 'Could not submit review' 
      //     }),
      //     'error'
      //   )
      // );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset the form to initial state and collapse it
    setIsExpanded(false);
    
    // If there was no existing rating, reset it to 0
    if (!existingReview) {
      setRating(0);
      methods.setValue('score', 0);
    } else {
      // Otherwise reset to the existing review's score
      setRating(existingReview.score);
      methods.setValue('score', existingReview.score);
    }
    
    // Reset the comment field
    methods.setValue('comment', existingReview?.text || '');
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="c-mobile-inline-review">
      <Form methods={methods} validationSchema={validationSchema} onSubmit={handleSubmit}>
        <div className="c-mobile-inline-review__stars-row">
          <div className="c-mobile-inline-review__stars">
            <RatingStars
              initialValue={rating}
              onChange={handleRatingChange}
              size="medium"
            />
          </div>
        </div>
        
        {isExpanded && (
          <>
          
            <div className="c-fieldset">
              <div className="c-compact-review__comment">
                <TextareaControlled
                  name="comment"
                  placeholder={intl.formatMessage({
                    id: 'containers.review.comment.placeholder',
                    defaultMessage: 'Tell us more about this user',
                  })}
                  className="c-mobile-inline-review__textarea"
                />
              </div>
            </div>
            
            <div className="c-fieldset">
              <div className="c-modal__buttons">
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !rating}
                  className="c-btn c-btn--primary c-btn--small"
                >
                  {intl.formatMessage({
                    id: 'containers.review.save',
                    defaultMessage: 'Save',
                  })}
                </Button>

                <Button
                  type="button"
                  onClick={handleCancel}
                  className="c-btn--secondary c-btn--small"
                >
                  {intl.formatMessage({
                    id: 'common.cancel',
                    defaultMessage: 'Cancel',
                  })}
                </Button>
              </div>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default MobileInlineChatReview; 